/**
 * "Yes, most days": a row per weekday over the listing's availability rules. Each window is saved as
 * soon as it is added, edited or removed (a rule that covers several weekdays is split so only the
 * edited day changes). "Same hours Monday to Friday" copies Monday's windows onto Tuesday to Friday.
 */

import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Badge } from '../../../../components/ui/badge';
import { Button } from '../../../../components/ui/button';
import {
  createAvailabilityRule,
  deleteAvailabilityRule,
  updateAvailabilityRule,
  type AvailabilityRule,
} from '../../../../lib/leasing.api';
import { LifecycleConfirmDialog } from '../shared';
import { formatTimeRange, WEEKDAY_NAMES } from './time-utils';
import { InlineError, LINK_BUTTON_CLASS, ShowingsCard, usePanelReport, type PanelReport } from './panel-support';
import { TimeSelect } from './TimeSelect';
import { useAvailabilityAction } from './useAvailabilityAction';
import {
  ruleWithoutDay,
  SAME_HOURS_TARGET_DAYS,
  validateWindow,
  weeklyHours,
  windowsByWeekday,
  WEEKDAYS,
  type DayWindow,
} from './weekly-hours';

interface WeeklyHoursPanelProps {
  listingId: string;
  isClosed: boolean;
  rules: AvailabilityRule[];
  report: PanelReport;
}

interface WindowForm {
  day: number;
  /** Set when editing an existing window. */
  ruleId: string | null;
  startTime: string;
  endTime: string;
}

export function WeeklyHoursPanel({ listingId, isClosed, rules, report }: WeeklyHoursPanelProps) {
  const { run, saving, error, clearError } = useAvailabilityAction(listingId);
  const [form, setForm] = useState<WindowForm | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<{ ruleId: string; day: number } | null>(null);
  const [confirmSameHours, setConfirmSameHours] = useState(false);
  usePanelReport(report, saving, form !== null);

  const byDay = windowsByWeekday(rules);
  const ruleById = new Map(rules.map((rule) => [rule.id, rule]));
  const hours = weeklyHours(rules);

  const openForm = (next: WindowForm) => {
    clearError();
    setFormError(null);
    setForm(next);
  };

  /** Drops `day` from a rule: deletes the rule when it was the only day, otherwise shrinks it. */
  const dropDayFromRule = async (rule: AvailabilityRule, day: number) => {
    const change = ruleWithoutDay(rule, day);
    if (change.delete) await deleteAvailabilityRule(listingId, rule.id);
    else await updateAvailabilityRule(listingId, rule.id, { weekdays: change.weekdays });
  };

  const submitForm = async () => {
    if (!form) return;
    const others = byDay[form.day]!.filter((w) => w.ruleId !== form.ruleId);
    const problem = validateWindow(form.startTime, form.endTime, others);
    if (problem) {
      setFormError(problem);
      return;
    }
    setFormError(null);
    const existing = form.ruleId ? ruleById.get(form.ruleId) : undefined;
    const ok = await run(async () => {
      if (!existing) {
        await createAvailabilityRule(listingId, {
          weekdays: [form.day],
          startTime: form.startTime,
          endTime: form.endTime,
          notes: null,
        });
      } else if (existing.weekdays.length === 1) {
        await updateAvailabilityRule(listingId, existing.id, { startTime: form.startTime, endTime: form.endTime });
      } else {
        // The rule covers other weekdays too: keep it for them and give this day its own window.
        await updateAvailabilityRule(listingId, existing.id, {
          weekdays: existing.weekdays.filter((d) => d !== form.day),
        });
        await createAvailabilityRule(listingId, {
          weekdays: [form.day],
          startTime: form.startTime,
          endTime: form.endTime,
          effectiveFrom: existing.effectiveFrom,
          effectiveUntil: existing.effectiveUntil,
          notes: existing.notes,
        });
      }
    }, 'Failed to save the window');
    if (ok) setForm(null);
  };

  const removeWindow = async () => {
    if (!confirmRemove) return;
    const rule = ruleById.get(confirmRemove.ruleId);
    if (!rule) {
      setConfirmRemove(null);
      return;
    }
    const ok = await run(() => dropDayFromRule(rule, confirmRemove.day), 'Failed to remove the window');
    if (ok) setConfirmRemove(null);
  };

  const windowKey = (w: DayWindow) => `${w.startTime}-${w.endTime}`;
  const mondayKeys = byDay[1]!.map(windowKey).join('|');
  const sameHoursAlready = SAME_HOURS_TARGET_DAYS.every((d) => byDay[d]!.map(windowKey).join('|') === mondayKeys);
  const needsSameHoursConfirm = SAME_HOURS_TARGET_DAYS.some((d) => byDay[d]!.length > 0 && !sameHoursAlready);

  const applySameHours = async () => {
    const monday = byDay[1]!;
    if (monday.length === 0 || sameHoursAlready) {
      setConfirmSameHours(false);
      return;
    }
    const ok = await run(async () => {
      for (const rule of rules.filter((r) => r.weekdays.some((d) => SAME_HOURS_TARGET_DAYS.includes(d)))) {
        const remaining = rule.weekdays.filter((d) => !SAME_HOURS_TARGET_DAYS.includes(d));
        if (remaining.length === 0) await deleteAvailabilityRule(listingId, rule.id);
        else await updateAvailabilityRule(listingId, rule.id, { weekdays: remaining });
      }
      for (const window of monday) {
        await createAvailabilityRule(listingId, {
          weekdays: [...SAME_HOURS_TARGET_DAYS],
          startTime: window.startTime,
          endTime: window.endTime,
          notes: null,
        });
      }
    }, 'Failed to copy Monday’s hours');
    if (ok) setConfirmSameHours(false);
  };

  const requestSameHours = () => {
    clearError();
    if (byDay[1]!.length === 0) {
      setFormError('Add Monday’s hours first, then copy them.');
      return;
    }
    setFormError(null);
    if (needsSameHoursConfirm) setConfirmSameHours(true);
    else void applySameHours();
  };

  return (
    <ShowingsCard
      data-testid="weekly-hours-panel"
      title="Weekly hours"
      action={
        rules.length > 0 ? (
          <Badge variant="success" data-testid="weekly-hours-badge">
            About {hours} {hours === 1 ? 'hour' : 'hours'} a week
          </Badge>
        ) : undefined
      }
    >
      {!isClosed && (
        <Button
          type="button"
          variant="outline"
          className="mb-4"
          data-testid="weekly-hours-same-weekdays"
          disabled={saving}
          onClick={requestSameHours}
        >
          Same hours Monday to Friday
        </Button>
      )}

      {rules.length === 0 && (
        <div
          role="status"
          data-testid="no-availability-rules-warning"
          className="mb-4 rounded-md border border-warning/25 bg-warning/10 p-3 text-sm font-medium text-warning"
        >
          No weekly hours yet. Prospects can&apos;t book a showing until you add some.
        </div>
      )}

      {(error || (formError && !form)) && (
        <div className="mb-3">
          <InlineError message={(error ?? formError)!} testId="weekly-hours-error" />
        </div>
      )}

      <ul className="divide-y divide-border">
        {WEEKDAYS.map((day) => {
          const windows = byDay[day]!;
          const formHere = form?.day === day ? form : null;
          return (
            <li key={day} data-testid={`weekly-day-${day}`} className="py-2">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="w-28 shrink-0 text-sm font-semibold text-foreground">{WEEKDAY_NAMES[day]}</span>
                <div className="flex flex-1 flex-wrap items-center gap-2">
                  {windows.length === 0 && <span className="text-sm text-muted-foreground">Not available</span>}
                  {windows.map((w) => (
                    <span
                      key={`${w.ruleId}-${day}`}
                      data-testid={`weekly-window-${w.ruleId}-${day}`}
                      className="inline-flex items-center rounded-md border border-border bg-muted/40 text-sm"
                    >
                      <button
                        type="button"
                        data-testid={`weekly-window-edit-${w.ruleId}-${day}`}
                        disabled={isClosed || saving}
                        aria-label={`Edit ${formatTimeRange(w.startTime, w.endTime)} on ${WEEKDAY_NAMES[day]}`}
                        onClick={() =>
                          openForm({ day, ruleId: w.ruleId, startTime: w.startTime, endTime: w.endTime })
                        }
                        className="min-h-[44px] rounded-l-md px-3 text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default"
                      >
                        {formatTimeRange(w.startTime, w.endTime)}
                      </button>
                      {!isClosed && (
                        <button
                          type="button"
                          data-testid={`delete-rule-${w.ruleId}-${day}`}
                          disabled={saving}
                          aria-label={`Remove ${formatTimeRange(w.startTime, w.endTime)} on ${WEEKDAY_NAMES[day]}`}
                          onClick={() => setConfirmRemove({ ruleId: w.ruleId, day })}
                          className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-r-md text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <X aria-hidden="true" className="h-4 w-4" />
                        </button>
                      )}
                    </span>
                  ))}
                  {!isClosed && !formHere && (
                    <button
                      type="button"
                      data-testid={`weekly-add-window-${day}`}
                      disabled={saving}
                      onClick={() => openForm({ day, ruleId: null, startTime: '09:00', endTime: '17:00' })}
                      className={LINK_BUTTON_CLASS}
                    >
                      <Plus aria-hidden="true" className="mr-1 h-4 w-4" />
                      {windows.length === 0 ? 'Add hours' : 'Add window'}
                    </button>
                  )}
                </div>
              </div>

              {formHere && (
                <form
                  data-testid="weekly-window-form"
                  className="mt-2 space-y-3 rounded-md border border-border bg-muted/20 p-3"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void submitForm();
                  }}
                >
                  <p className="text-sm font-medium text-foreground">
                    {formHere.ruleId ? 'Change this window' : 'Add a window'} on {WEEKDAY_NAMES[day]}
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <TimeSelect
                      id={`weekly-window-start-${day}`}
                      label="From"
                      value={formHere.startTime}
                      disabled={saving}
                      data-testid="weekly-window-start"
                      onChange={(startTime) => setForm({ ...formHere, startTime })}
                    />
                    <TimeSelect
                      id={`weekly-window-end-${day}`}
                      label="To"
                      value={formHere.endTime}
                      disabled={saving}
                      data-testid="weekly-window-end"
                      onChange={(endTime) => setForm({ ...formHere, endTime })}
                    />
                  </div>
                  {formError && <InlineError message={formError} testId="weekly-window-error" />}
                  <div className="flex gap-2">
                    <Button type="submit" data-testid="weekly-window-save" disabled={saving}>
                      {saving ? 'Saving…' : formHere.ruleId ? 'Save window' : 'Add window'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      data-testid="weekly-window-cancel"
                      disabled={saving}
                      onClick={() => {
                        setForm(null);
                        setFormError(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              )}
            </li>
          );
        })}
      </ul>

      <LifecycleConfirmDialog
        open={confirmRemove !== null}
        onOpenChange={(next) => {
          if (!next) setConfirmRemove(null);
        }}
        title="Remove this window?"
        description={
          confirmRemove
            ? `Prospects will no longer be able to book showings on ${WEEKDAY_NAMES[confirmRemove.day]} during these hours.`
            : ''
        }
        confirmLabel="Remove window"
        pendingLabel="Removing…"
        onConfirm={() => void removeWindow()}
        isSubmitting={saving}
        destructive
        testIdPrefix="delete-rule-confirm"
      >
        {error && confirmRemove && <p className="mb-3 text-sm text-destructive" role="alert">{error}</p>}
      </LifecycleConfirmDialog>

      <LifecycleConfirmDialog
        open={confirmSameHours}
        onOpenChange={(next) => {
          if (!next) setConfirmSameHours(false);
        }}
        title="Replace Tuesday to Friday?"
        description="Tuesday to Friday will use Monday’s hours instead of the hours you set for them."
        confirmLabel="Use Monday’s hours"
        pendingLabel="Saving…"
        onConfirm={() => void applySameHours()}
        isSubmitting={saving}
        testIdPrefix="weekly-same-hours-confirm"
      >
        {error && confirmSameHours && <p className="mb-3 text-sm text-destructive" role="alert">{error}</p>}
      </LifecycleConfirmDialog>
    </ShowingsCard>
  );
}
